import { Injectable } from '@angular/core';
import { KlaDataService } from './kla.data.service';
import { Observable } from 'rxjs';
import { NewsConfigurations } from '../news/news.configurations';
import { HttpClient } from '@angular/common/http';
import { EventConfigurations } from '../event/event.configurations';
import { Image, PostsResponse } from './kla.data.models';
import { Post } from '../post-item/post.model';

/**
 * Created by drveni92 on 10/11/2018.
 */
@Injectable({providedIn: "root"})
export class KlaHttpService {
  constructor(private dataService: KlaDataService, private http: HttpClient) { }

  public getImagePath(image: string, width: number) {
    if (image) {
      if (image.toString().indexOf('..') > -1 || image.toString().indexOf('http') > -1) {
        return image;
      } else {
        const config = this.dataService.getImageConfig();
        if (!config) {
          console.error('Could not load image config from Data Service.');
          return null;
        }
        return config.server + '/' + config.version + '/images/' + image + '/thumb/w' + width;
      }
    } else {
      return null;
    }
  }

  public getCroppedImagePath(imageConfig: Image): any {
    
    const cropConfig = imageConfig?.crop_config || imageConfig?.cropConfig;
    if (!cropConfig?.position) {
      return this.getImagePath(imageConfig?.id, imageConfig?.width);
    }

    const croppedX = cropConfig.position.x1;
    const croppedY = cropConfig.position.y1;
    const croppedWidth = cropConfig.position.x2 - cropConfig.position.x1;
    const croppedHeight = cropConfig.position.y2 - cropConfig.position.y1;
    const croppedImageWidth = cropConfig.width;
    const croppedImageHeight = cropConfig.height;

    let imageUrl: string = this.getImagePath(imageConfig.id, imageConfig.width);
    imageUrl = imageUrl + '_rcx' + croppedX + '_rcy' + croppedY + '_rcw' + croppedWidth + '_rch' + croppedHeight + '_ciw' + croppedImageWidth + '_cih' + croppedImageHeight;
    return imageUrl;
  }

  public getChartPath(name: string, id: string): string {
    const config = this.dataService.getApiConfig();
    return config.server + '/' + config.version + '/' + config.chartsPath + '?chartConfig=' + encodeURIComponent(id) + '&name=' + name;
  }

  public getHighchartPath(id: string): string {
    const config = this.dataService.getApiConfig();
    return config.server + '/' + config.version + '/' + config.highchartsPath + '?chartConfig=' + encodeURIComponent(id);
  }

  public getPostsPath(): string {
    const config = this.dataService.getApiConfig();
    return config.server + '/' + config.version + '/' + config.postsPath;
  }

  public getSingleBlogPath(): string {
    const config = this.dataService.getApiConfig();
    return config.server + '/' + config.version + '/' + config.singleBlogPath;
  }

  public getNewsPath(id: string): string {
    const config = this.dataService.getApiConfig();
    return config.server + '/' + config.version + '/' + config.newsPath + '?newsIds=' + id;
  }

  public getAnalyticsPath(id: number): string {
    const config = this.dataService.getApiConfig();
    // return 'http://api.kla.local' + '/' + config.version + '/websites/' + id + '/analytics';
    if(config) 
      return config.server + '/' + config.version + '/websites/' + id + '/analytics';
    else 
      return "";
  }

  public getEventsPath(id: string): string {
    const config = this.dataService.getApiConfig();
    return config.server + '/' + config.version + '/' + config.eventsPath + '?id=' + id;
  }

  public getSingleEventPath(): string {
    const config = this.dataService.getApiConfig();
    return config.server + '/' + config.version + '/' + config.singleEventPath;
  }

  public getMaintenancePath(id: number): string {
    const config = this.dataService.getApiConfig();
    // return 'http://api.kla.local' + '/' + config.version + '/websites/' + id + '/maintenance';
    return config.server + '/' + config.version + '/websites/' + id + '/maintenance';
  }

  public getImagePathByHeight(image: string, height: number) {
    if (image) {
      if (image.indexOf('..') > -1 || image.indexOf('http') > -1) {
        return image;
      } else {
        const config = this.dataService.getImageConfig();
        return config.server + '/' + config.version + '/images/' + image + '/thumb/h' + height;
      }
    } else {
      return null;
    }
  }

  public getChartData(chartName: string, id: string): Observable<any> {
    return Observable.create(obs => {
      this.http.get(this.getChartPath(chartName, id)).subscribe(
        (data) => {
          obs.next(data);
          obs.complete();
        },
        (err) => {
          console.error('Failed to find chart data, application will use already cashed data from the json.', err);
          obs.next(this.dataService.getChartConfig(chartName, id));
          obs.complete();
        }
      );
    });
  }

  public getHighchartData(id: string): Observable<any> {
    return Observable.create(obs => {
      this.http.get(this.getHighchartPath(id)).subscribe(
        (data) => {
          obs.next(data);
          obs.complete();
        },
        (err) => {
          console.error('Failed to find highchart data, application will use already cashed data from the json.', err);
          obs.complete();
        }
      );
    });
  }

  public getPostsData(params: string = ''): Observable<PostsResponse> {
    return Observable.create(obs => {
      this.http.get(this.getPostsPath() + params, {observe: 'response'}).subscribe(
        (data) => {
          obs.next(data);
          obs.complete();
        },
        (err) => {
          console.error('Failed to find blog, application will use already cashed data from the json.', err);
          obs.next(this.dataService.getBlogConfig());
          obs.complete();
        }
      );
    });
  }

  public getSingleBlogData(id: string): Observable<Post> {
    return Observable.create(obs => {
      this.http.get(this.getSingleBlogPath() + id).subscribe(
        (data) => {
          obs.next(data);
          obs.complete();
        },
        (err) => {
          console.error('Failed to find blog, application will use already cashed data from the json.', err);
          obs.next(this.dataService.getBlogConfig());
          obs.complete();
        }
      );
    });
  }

  public getNewsData(id: string): Observable<Array<NewsConfigurations>> {
    return Observable.create(obs => {
      this.http.get(this.getNewsPath(id)).subscribe(
        (data) => {
          obs.next(data);
          obs.complete();
        },
        (err) => {
          console.error('Failed to find news, application will use already cashed data from the json.', err);
          obs.next(this.dataService.getNewsConfig(id));
          obs.complete();
        }
      );
    });
  }

  public getEventsData(id: string): Observable<Array<EventConfigurations>> {
    return Observable.create(obs => {
      this.http.get(this.getEventsPath(id)).subscribe(
        (data) => {
          obs.next(data);
          obs.complete();
        },
        (err) => {
          console.error('Failed to find events, application will use already cashed data from the json.', err);
          obs.next(this.dataService.getEventsConfig(id));
          obs.complete();
        }
      );
    });
  }

  public getSingleEventData(id: string): Observable<Post> {
    return Observable.create(obs => {
      this.http.get(this.getSingleEventPath() + id).subscribe(
        (data) => {
          obs.next(data);
          obs.complete();
        },
        (err) => {
          console.error('Failed to find blog, application will use already cashed data from the json.', err);
          obs.next(this.dataService.getEventsConfig(id));
          obs.complete();
        }
      );
    });
  }

  public checkIsMaintenance(id: number) {
    return this.http.get(this.getMaintenancePath(id));
  }

  public checkIsAnalyticsActive(id: number) {
    return Observable.create(obs => {
      this.http.get(this.getAnalyticsPath(id)).subscribe(
        (data) => {
          obs.next(data);
          obs.complete();
        },
        (err) => {
          console.error('Failed to find website.', err);
          // obs.next(this.dataService.getEventsConfig(id));
          obs.complete();
        }
      );
    });
  }

}
